/**
 * Reconciles a locally-started game with the server.
 *
 * PRD 8.2 requires the full live-scoring flow to work with no network, so a
 * game is created locally and played from local state. The server may not hear
 * about it until the next morning. Every step here is therefore:
 *
 *  - idempotent, so calling it on every flush is free;
 *  - soft-failing, so being offline is a no-op rather than an error;
 *  - order-dependent, so it stops at the first step it cannot complete.
 */

export interface SessionStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

export interface ReconcileInput {
  /** Locally-generated game id, also used as the idempotency key. */
  localGameId: string;
  /** Locally-generated group id, so each group maps to its own server group. */
  localGroupId: string;
  /** Local player ids in playing order. */
  order: string[];
  /** Local player id -> display name as typed at setup. */
  names: Record<string, string>;
  groupName: string;
}

export interface Deps {
  baseUrl?: string;
  store: SessionStore;
  fetch?: typeof globalThis.fetch;
  /** Account name used on first run. */
  username: () => string;
}

const KEY = {
  token: "kniffel.token",
  userId: "kniffel.userId",
  /** Keyed by the *local* group id. A single shared key mapped every local
   *  group onto one server group, so a second group's players and games all
   *  landed in the first one. */
  group: (localId: string) => `kniffel.group.${localId}`,
  person: (localId: string) => `kniffel.person.${localId}`,
  game: (localId: string) => `kniffel.game.${localId}`,
};

/**
 * Usernames are unique across the whole persons table (see DECISIONS.md D-3),
 * which is what makes one Anna the same Anna in every group. Taken literally
 * from free-text setup names, that would also make *another household's* Anna
 * the same person and silently merge their statistics. Scoping the username to
 * the account keeps D-3's intent within an account and stops the collision
 * between accounts. The display name is unaffected.
 */
export function usernameFor(userId: string, displayName: string): string {
  const slug = displayName
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `${userId.slice(0, 8)}-${slug || "spieler"}`;
}

export class Session {
  readonly #base: string;
  readonly #store: SessionStore;
  readonly #fetch: typeof globalThis.fetch;
  readonly #username: () => string;

  constructor(deps: Deps) {
    this.#base = deps.baseUrl ?? "/api/kniffel";
    this.#store = deps.store;
    // Bound on purpose: a browser's fetch throws "Illegal invocation" when it
    // is called as a method of anything but the window. Node's does not care,
    // so every test here passed while the real app could never send a thing.
    this.#fetch = deps.fetch ?? globalThis.fetch.bind(globalThis);
    this.#username = deps.username;
  }

  get token(): string | null {
    return this.#store.get(KEY.token);
  }

  /** Server id for a local game, once it has one. */
  serverGameId(localGameId: string): string | null {
    return this.#store.get(KEY.game(localGameId));
  }

  async #post<T>(path: string, body: unknown, auth = true): Promise<T | null> {
    const token = this.token;
    if (auth && !token) return null;
    try {
      const res = await this.#fetch(`${this.#base}${path}`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...(auth && token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) return null;
      return (await res.json()) as T;
    } catch {
      // Offline. Nothing is wrong; there is simply nothing to do yet.
      return null;
    }
  }

  /** Register this device once. Returns false when it could not be done yet. */
  async ensureIdentity(): Promise<boolean> {
    if (this.token) return true;
    const out = await this.#post<{ token: string; user: { id: string } }>(
      "/auth/register",
      { username: this.#username(), device: "Browser" },
      false,
    );
    if (!out?.token) return false;
    this.#store.set(KEY.token, out.token);
    this.#store.set(KEY.userId, out.user.id);
    return true;
  }

  /** Server id for a local group, once it has one. */
  serverGroupId(localGroupId: string): string | null {
    return this.#store.get(KEY.group(localGroupId));
  }

  async ensureGroup(localGroupId: string, name: string): Promise<string | null> {
    const existing = this.#store.get(KEY.group(localGroupId));
    if (existing) return existing;
    const out = await this.#post<{ id: string }>("/groups", { name });
    if (!out?.id) return null;
    this.#store.set(KEY.group(localGroupId), out.id);
    return out.id;
  }

  /** Push a renamed group. Silent when offline; the name is local until then. */
  async renameGroup(localGroupId: string, name: string): Promise<boolean> {
    const serverId = this.serverGroupId(localGroupId);
    const token = this.token;
    if (!serverId || !token) return false;
    try {
      const res = await this.#fetch(`${this.#base}/groups/${serverId}`, {
        method: "PATCH",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify({ name }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  async ensurePerson(groupId: string, localId: string, displayName: string): Promise<string | null> {
    const existing = this.#store.get(KEY.person(localId));
    if (existing) return existing;
    const userId = this.#store.get(KEY.userId) ?? "";
    const out = await this.#post<{ id: string }>(`/groups/${groupId}/players`, {
      username: usernameFor(userId, displayName),
      displayName,
    });
    if (!out?.id) return null;
    this.#store.set(KEY.person(localId), out.id);
    return out.id;
  }

  /**
   * Walk the whole chain. Returns the server's game id once it exists, or null
   * if any step could not complete -- in which case the turns stay queued and
   * the next flush tries again.
   */
  async reconcile(input: ReconcileInput): Promise<string | null> {
    const already = this.serverGameId(input.localGameId);
    if (already) return already;

    if (!(await this.ensureIdentity())) return null;

    const groupId = await this.ensureGroup(input.localGroupId, input.groupName);
    if (!groupId) return null;

    const personIds: string[] = [];
    for (const localId of input.order) {
      const personId = await this.ensurePerson(
        groupId,
        localId,
        input.names[localId] ?? localId,
      );
      // Stop at the first gap: a game created with a short order would put the
      // turns against the wrong players for the rest of the evening.
      if (!personId) return null;
      personIds.push(personId);
    }

    const game = await this.#post<{ id: string }>(`/groups/${groupId}/games`, {
      order: personIds,
      // The local id doubles as the idempotency key, so a retry after a dropped
      // response returns the same game rather than creating a second one.
      clientId: input.localGameId,
    });
    if (!game?.id) return null;

    this.#store.set(KEY.game(input.localGameId), game.id);
    return game.id;
  }
}

/** localStorage-backed store; degrades to memory when it is unavailable. */
export function localSessionStore(): SessionStore {
  const memory = new Map<string, string>();
  return {
    get(key) {
      try {
        return globalThis.localStorage?.getItem(key) ?? memory.get(key) ?? null;
      } catch {
        return memory.get(key) ?? null;
      }
    },
    set(key, value) {
      memory.set(key, value);
      try {
        globalThis.localStorage?.setItem(key, value);
      } catch {
        // Private mode or quota: the session still works for this run.
      }
    },
  };
}
